import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  Typography,
  Divider,
  Paper,
  Chip,
  Button
} from '@mui/material';
import QrCodeIcon from '@mui/icons-material/QrCode';
import PrintIcon from '@mui/icons-material/Print';
import { Opinion } from '../../interface/Opinion';
import { getOpinionById } from '../../services/OpinionService';
import QRCode from 'react-qr-code';

const OpinionBrochure: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [opinion, setOpinion] = useState<Opinion | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadOpinion = async () => {
      if (!id) return;
      try {
        const data = await getOpinionById(id);
        setOpinion(data);
      } catch (err: any) {
        console.error('Error loading opinion for brochure:', err);
        setError(err?.response?.data?.error || 'Failed to load opinion');
      } finally {
        setLoading(false);
      }
    };

    loadOpinion();
  }, [id]);

  const opinionUrl =
    typeof window !== 'undefined' && id
      ? `${window.location.origin}/opinions/${id}`
      : '';

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#f5f7fa'
        }}
      >
        <Typography variant="h6">Preparing brochure...</Typography>
      </Box>
    );
  }

  if (!opinion || error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#f5f7fa'
        }}
      >
        <Typography variant="h6" color="error">
          {error || 'Opinion not found'}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>Opinion Brochure | OpinLoop</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <style>
        {`
          @media print {
            body {
              background: #ffffff !important;
            }
            .no-print {
              display: none !important;
            }
            .brochure-page {
              box-shadow: none !important;
              margin: 0 !important;
              width: auto !important;
            }
          }
        `}
      </style>

      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: '#e5e7eb',
          py: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Container maxWidth="md">
          {/* Top actions (screen only) */}
          <Box
            className="no-print"
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2
            }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              Brochure preview — you can print this and use it in your shop or
              office.
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
            >
              Print brochure
            </Button>
          </Box>

          {/* Brochure page */}
          <Paper
            elevation={3}
            className="brochure-page"
            sx={{
              p: 4,
              borderRadius: 2,
              bgcolor: '#ffffff',
              color: '#111827'
            }}
          >
            {/* Header with Logo and QR */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                mb: 3
              }}
            >
              {/* Logo */}
              <Box sx={{ mb: 3 }}>
                <Box
                  component="img"
                  src="/images/logo.png"
                  alt="OpinLoop"
                  sx={{
                    height: { xs: 48, sm: 56 },
                    width: 'auto',
                    maxWidth: '100%',
                    objectFit: 'contain',
                    borderRadius: 2
                  }}
                />
              </Box>
              
              {/* QR Code */}
              <Box
                sx={{
                  mb: 2,
                  p: 1.5,
                  borderRadius: 2,
                  border: '2px solid #e5e7eb'
                }}
              >
                <QRCode
                  value={opinionUrl}
                  size={160}
                  style={{
                    height: 'auto',
                    maxWidth: '100%',
                    width: '100%'
                  }}
                  viewBox="0 0 256 256"
                />
              </Box>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <QrCodeIcon fontSize="small" />
                <Typography variant="subtitle2" fontWeight={600}>
                  Scan to view & respond online
                </Typography>
              </Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ maxWidth: 360 }}
              >
                Customers can scan this QR code to see this opinion on
                OpinLoop, share it, and answer questions digitally.
              </Typography>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Opinion meta */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                {opinion.category || 'Opinion'}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                Posted by: {opinion.username}
              </Typography>
              {opinion.targetAudience && opinion.targetAudience.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {opinion.targetAudience.map((aud) => (
                    <Chip key={aud} label={aud} size="small" />
                  ))}
                </Box>
              )}
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Main content */}
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h5"
                fontWeight={700}
                gutterBottom
                sx={{ textAlign: 'left' }}
              >
                Discussion Topic
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: '1.05rem',
                  lineHeight: 1.8,
                  whiteSpace: 'pre-wrap'
                }}
              >
                {opinion.content}
              </Typography>
            </Box>

            {/* Questions & options for print */}
            {opinion.questions && opinion.questions.length > 0 && (
              <>
                <Divider sx={{ mb: 3 }} />
                <Box>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    gutterBottom
                    sx={{ textAlign: 'left' }}
                  >
                    Questions for your customers
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Ask customers to tick one option for each question. They can
                    also scan the QR code and answer online.
                  </Typography>

                  {opinion.questions.map((q, qIndex) => (
                    <Box key={qIndex} sx={{ mt: 2 }}>
                      <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        gutterBottom
                      >
                        Q{qIndex + 1}. {q.questionText}
                      </Typography>
                      <Box sx={{ ml: 2 }}>
                        {q.options.map((opt, optIndex) => (
                          <Box
                            key={optIndex}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              mb: 0.75
                            }}
                          >
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                borderRadius: 0.5,
                                border: '1px solid #4b5563',
                                mr: 1.5
                              }}
                            />
                            <Typography variant="body2">{opt.text}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </>
            )}

            {/* Footer */}
            <Box sx={{ mt: 4 }}>
              <Divider sx={{ mb: 1.5 }} />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', textAlign: 'right' }}
              >
                Generated with OpinLoop — {opinionUrl}
              </Typography>
            </Box>
          </Paper>
        </Container>
      </Box>
    </>
  );
};

export default OpinionBrochure;

